/* eslint-disable react-i18n/no-dynamic-translation-keys */
import React, { useRef, useCallback, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import Select from "react-select";
import { Button as BtnPrimary } from "@talentbait/shared-components";

import { Tabs } from "../../../../../Components/Tabs/Tabs";
import { TabsContextProvider } from "../../../../../context/Tabs.context";

import TemplatePostActions from "../../TemplatePost/TemplatePostActions";

import {
  CustomSidePanel,
  MultiInputs,
  SingleInputWraper,
  InputLabel,
  EditorUpdateActions,
  ColorInputStyles,
  ColorSelector,
  TextInputStyles,
  MaxCharacters,
  ColorWrapper
} from "./styles";

import { DEFAULT_JINGLES } from "../../VideoGenerator/VideoGenerator__Editor/VideoGenerator__Editor.constants";
import useTabsData from "./hooks/useTabsData";
import ImageVideoInputPreview from "./ImageVideoInputPreview/ImageVideoInputPreview";
import { mapInputPropsValues, resetOldInputProps } from "../TemplatePost__Preview/utils";

/**
 * SingleColorInput component
 *
 * @param {Object} params
 * @param {string} params.value - Color value
 * @param {(key: any, value: any) => void} params.onChange - On change handler
 * @param {string} params.field - Field name
 * @param {string} params.label - Label
 */
const SingleColorInput = ({ value = "#000000", onChange, field, label }) => {
  const ref = useRef();
  return (
    <ColorWrapper>
      <InputLabel>
        <Trans>{label}</Trans>
      </InputLabel>
      <ColorSelector color={value} onClick={() => ref?.current?.click()}>
        <ColorInputStyles
          ref={ref}
          type="color"
          value={value}
          onChange={(e) => onChange(field, e.target.value)}
          aria-hidden="true"
        />
      </ColorSelector>
    </ColorWrapper>
  );
};

/**
 * ColorInputs component
 *
 * @param {Object} params
 * @param {import("@talentbait/remotion-templates-talentbait").ColorInput[]} params.colorInputs
 */
const ColorInputs = ({ colorInputs, hideNewDefaultStyles }) => {
  if (!colorInputs?.length) return <></>;

  return (
    <MultiInputs>
      {colorInputs.map((colorInput, index) => {
        if (
          ["secondaryTextColor", "secondaryBackgroundColor"].includes(colorInput.key) &&
          hideNewDefaultStyles
        ) {
          return null;
        }
        return <SingleColorInput {...colorInput} field={colorInput.key} key={index} />;
      })}
    </MultiInputs>
  );
};

/**
 * ArrayInputs component
 *
 * @param {Object} params
 * @param {import("@talentbait/remotion-templates-talentbait").ArrayInput[]} params.arrayInputs
 */
const ArrayInputs = ({ arrayInputs, onChange }) => {
  if (arrayInputs.length === 0) return null;

  const defaultValue = (length) => Array.from({ length: length }, () => "");

  const arrayElements = arrayInputs[0].value || defaultValue(arrayInputs[0].elements.length);
  let inputs = arrayElements;

  const onChangeBenefit = (key, value) => {
    let modifiedElements = [...inputs];

    modifiedElements[key] = value;

    onChange(arrayInputs[0].key, modifiedElements);
  };

  return (
    <MultiInputs style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {arrayInputs[0].elements.map((element, index) => (
        <SingleInputWraper key={element.label}>
          <InputLabel>{`${element.label}`}</InputLabel>
          <TextInputStyles
            value={inputs[index]}
            onChange={(e) => onChangeBenefit(index, e.target.value)}
            maxLength={element.maxCharacters}
          />
          {inputs[index] && element.maxCharacters && (
            <MaxCharacters>{`${inputs[index].length} / ${element.maxCharacters}`}</MaxCharacters>
          )}
        </SingleInputWraper>
      ))}
    </MultiInputs>
  );
};

/**
 * TextInputs component
 *
 * @param {Object} params
 * @param {import("@talentbait/remotion-templates-talentbait").TextInput[]} params.textInputs
 */
const TextInputs = ({ textInputs, hideNewDefaultStyles }) => {
  return (
    <MultiInputs style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {textInputs.map(
        (
          { value, onChange, key, label, maxCharacters, isVerticalOrientation, templateId },
          index
        ) => {
          if (!isVerticalOrientation && key === "CTAText" && templateId !== "TikTokVideo")
            return null;
          if (key === "secondaryText" && hideNewDefaultStyles) return null;

          return (
            <SingleInputWraper key={index}>
              <InputLabel>
                <Trans>{label}</Trans>
              </InputLabel>
              <TextInputStyles
                value={value}
                onChange={(e) => onChange(key, e.target.value)}
                maxLength={maxCharacters}
              />
              {value && maxCharacters && (
                <MaxCharacters>{`${value.length} / ${maxCharacters}`}</MaxCharacters>
              )}
            </SingleInputWraper>
          );
        }
      )}
    </MultiInputs>
  );
};

/**
 * ImageInputs component
 *
 * @param {Object} params
 * @param {import("@talentbait/remotion-templates-talentbait").ImageInput[]} params.imageInputs - Image inputs
 * @param {() => void} params.closeTemplateEditor - Close template editor
 */
const ImageInputs = ({ imageInputs, closeTemplateEditor }) => {
  return (
    <MultiInputs>
      {imageInputs.map(({ value, key, label }, index) => (
        <SingleInputWraper key={index}>
          <InputLabel>
            <Trans>{label}</Trans>
          </InputLabel>
          <ImageVideoInputPreview
            asset={value || "/images/bild-vorschau.png"}
            closeTemplateEditor={closeTemplateEditor}
            assetKey={key}
          />
        </SingleInputWraper>
      ))}
    </MultiInputs>
  );
};

/**
 * VideoInputs component
 *
 * @param {Object} params
 * @param {import("@talentbait/remotion-templates-talentbait").VideoInput[]} params.videoInputs - Video inputs
 * @param {() => void} params.closeTemplateEditor - Close template editor
 */
const VideoInputs = ({ videoInputs, closeTemplateEditor }) => {
  return (
    <MultiInputs>
      {videoInputs.map(({ value, key, label }, index) => (
        <SingleInputWraper key={index}>
          <InputLabel>
            <Trans>{label}</Trans>
          </InputLabel>
          <ImageVideoInputPreview
            asset={value || "/images/bild-vorschau.png"}
            closeTemplateEditor={closeTemplateEditor}
            assetKey={key}
          />
        </SingleInputWraper>
      ))}
    </MultiInputs>
  );
};

/**
 * AudioInputs component
 *
 * @param {Object} params
 * @param {import("@talentbait/remotion-templates-talentbait").AudioInput[]} params.audioInputs
 */
const AudioInputs = ({ audioInputs }) => {
  const { t } = useTranslation();
  const getAudioLabel = (audioUrl) => {
    if (DEFAULT_JINGLES) {
      const audio = DEFAULT_JINGLES.find((jingle) => jingle.value === audioUrl);
      return audio ? audio : "Background Music";
    }
  };

  return (
    <MultiInputs style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {audioInputs.map(({ value, onChange, key, label }, index) => (
        <SingleInputWraper key={index}>
          <InputLabel>
            <Trans>{label}</Trans>
          </InputLabel>
          <div style={{ width: "100%" }}>
            <Select
              id="audioUrl"
              options={DEFAULT_JINGLES}
              onChange={(audio) => onChange(key, audio.value)}
              value={getAudioLabel(value)}
              placeholder={t(label)}
            />
          </div>
        </SingleInputWraper>
      ))}
    </MultiInputs>
  );
};

/**
 * SelectInputs component
 *
 * @param {Object} params
 * @param {import("@talentbait/remotion-templates-talentbait").SelectInput[]} params.selectInputs
 */
const SelectInputs = ({ selectInputs, showVerticalImage, isSnapchatChannel }) => {
  const { t } = useTranslation();
  const getSelectInputValue = (selectedValue, options, defaultOption) => {
    if (!options) return;

    const inputValue = options.find((option) => option.value === selectedValue);
    return inputValue ? inputValue : defaultOption;
  };

  const orientationsFilteredOptions = (options) => {
    if (!showVerticalImage) {
      return options.filter((option) => option.value !== "vertical");
    }

    return options;
  };

  if (isSnapchatChannel) return null;

  return (
    <MultiInputs style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {selectInputs.map(({ options, key, label, onChange, value }, index) => {
        const isOrientation = key === "orientation";

        return (
          <SingleInputWraper key={index}>
            <InputLabel>
              <Trans>{label}</Trans>
            </InputLabel>
            <div style={{ width: "100%" }}>
              <Select
                id={key}
                options={isOrientation ? orientationsFilteredOptions(options) : options}
                onChange={(option) => onChange(key, option.value)}
                value={getSelectInputValue(value, options, options[0])}
                placeholder={t(label)}
              />
            </div>
          </SingleInputWraper>
        );
      })}
    </MultiInputs>
  );
};

const hasInputs = (inputs) => inputs.length > 0;

/**
 * SceneEditor component
 *
 * @param {Object} params
 * @param {import("@talentbait/remotion-templates-talentbait").Scene} params.scene - Scene object
 * @param {Object} params.inputProps - Input props
 * @param {(key: any, value: any) => void} params.onChange - On change handler
 * @param {() => void} params.closeTemplateEditor - Close template editor
 */
export const SceneEditor = ({
  scene,
  inputProps,
  showVerticalImage,
  isSnapchatChannel,
  isOldCampaign,
  templateId,
  onChange,
  closeTemplateEditor
}) => {
  let { inputs = [] } = scene;
  const updatedProps = mapInputPropsValues(inputProps);
  const hideNewDefaultStyles = isOldCampaign && templateId === "DefaultImage";

  const isVerticalOrientation = inputs.some(
    ({ key }) => key === "orientation" && inputProps[key] === "vertical"
  );

  useEffect(() => {
    if (isSnapchatChannel) {
      onChange("orientation", "vertical");
    }
  }, [isSnapchatChannel]);

  inputs = inputs.map((singleInput) => ({
    ...singleInput,
    value: updatedProps[singleInput.key],
    onChange,
    isVerticalOrientation,
    templateId
  }));

  const colorInputs = inputs.filter(({ type }) => type === "color");
  const textInputs = inputs.filter(({ type }) => type === "text");
  const imageInputs = inputs.filter(({ type }) => type === "image");
  const arrayInputs = inputs.filter(({ type }) => type === "array");
  const audioInputs = inputs.filter(({ type }) => type === "audio");
  const videoInputs = inputs.filter(({ type }) => type === "video");
  const selectInputs = inputs.filter(({ type }) => type === "select");

  return (
    <>
      {hasInputs(imageInputs) && (
        <ImageInputs imageInputs={imageInputs} closeTemplateEditor={closeTemplateEditor} />
      )}
      {hasInputs(videoInputs) && (
        <VideoInputs videoInputs={videoInputs} closeTemplateEditor={closeTemplateEditor} />
      )}
      {hasInputs(colorInputs) && (
        <ColorInputs colorInputs={colorInputs} hideNewDefaultStyles={hideNewDefaultStyles} />
      )}
      {hasInputs(textInputs) && (
        <TextInputs textInputs={textInputs} hideNewDefaultStyles={hideNewDefaultStyles} />
      )}
      {hasInputs(arrayInputs) && (
        <ArrayInputs arrayInputs={arrayInputs} inputProps={inputProps} onChange={onChange} />
      )}
      {hasInputs(audioInputs) && <AudioInputs audioInputs={audioInputs} />}
      {hasInputs(selectInputs) && (
        <SelectInputs
          selectInputs={selectInputs}
          showVerticalImage={showVerticalImage}
          isSnapchatChannel={isSnapchatChannel}
        />
      )}
    </>
  );
};

/**
 * TemplatePostEditor component
 *
 * @param {Object} params
 * @param {import("@talentbait/remotion-templates-talentbait").MediaTemplate} params.template - Template object
 * @param {Object} params.inputProps - Input props
 * @param {boolean} params.isTemplateEditorOpen - Is template editor open
 * @param {() => void} params.closeTemplateEditor - Close template editor
 * @param {(inputProps: any) => void} params.updateInputProps - Update input props
 * @param {() => void} params.saveInputProps - Save input props
 */
const TemplatePostEditor = ({
  showVerticalImage = false,
  showTikTokVideoTemplate = false,
  isSnapchatChannel = false,
  template,
  inputProps,
  isTemplateEditorOpen = true,
  allowImages = true,
  allowVideos = true,
  closeTemplateEditor,
  updateInputProps,
  saveInputProps
}) => {
  const { t } = useTranslation();
  const defaultTemplateTab = template.type === "video" ? "VideoTemplates" : "ImageTemplates";

  const onChange = useCallback(
    (key, value) => {
      const updatedProps = resetOldInputProps(inputProps, key, value);

      // Update inputProps
      updateInputProps(updatedProps);

      // Handle orientation changes specifically
      if (key === "orientation") {
        TemplatePostActions.updateInputProps({ orientation: value });
      }
    },
    [inputProps, updateInputProps]
  );

  const onSave = useCallback(() => {
    saveInputProps();
  }, [saveInputProps]);

  const onCancel = useCallback(() => {
    closeTemplateEditor();
  }, [closeTemplateEditor]);

  const tabsData = useTabsData({
    allowImages,
    allowVideos,
    template,
    onChange,
    showVerticalImage,
    isSnapchatChannel,
    showTikTokVideoTemplate,
    inputProps,
    closeTemplateEditor,
    t
  });

  return (
    <CustomSidePanel
      active={isTemplateEditorOpen}
      side="right"
      offset={10}
      height="auto"
      width="390px"
      zIndex={1}
      headline=""
      id="template-editor-panel"
    >
      <TabsContextProvider key={defaultTemplateTab} defaultTab={defaultTemplateTab}>
        <Tabs data={tabsData} />
        <EditorUpdateActions>
          <BtnPrimary mr={10} type="tertiary" variant="outline" onClick={onCancel}>
            {t("Cancel")}
          </BtnPrimary>
          <BtnPrimary type="primary" onClick={onSave}>
            {t("Select")}
          </BtnPrimary>
        </EditorUpdateActions>
      </TabsContextProvider>
    </CustomSidePanel>
  );
};

export default TemplatePostEditor;
